import { db } from '../instant';
import { id } from '@instantdb/react';
import type { PackingTask } from '../schema';
import {
    aggregateTaskDeltas,
    validateBatchTaskCompletion,
    computeSourceQuantityAfterChange,
    computeNewSourceQuantity,
    computeDestQuantityAfterChange,
    computeNewDestQuantity,
    type TaskSourceDestRow,
} from './taskCompletionLogic';
import { filterValidItemLocations, filterValidPackingTaskEndpoints } from './instantDbGuards';

/**
 * A flat item location entry built from preloaded useQuery data. Passed into
 * task completion functions so they never need to call db.queryOnce (which
 * requires a network connection and times out when offline).
 */
export type ItemLocationForLookup = {
    id: string;
    quantity?: number;
    itemTypeId: string;
    event?: { id: string } | null;
    storageLocation?: { id: string } | null;
};

function findItemLocation(
    allItemLocations: ItemLocationForLookup[],
    itemTypeId: string,
    eventId: string | null,
    storageLocationId: string | null
): ItemLocationForLookup | null {
    return (
        allItemLocations.find(loc => {
            if (loc.itemTypeId !== itemTypeId) return false;
            const locEventId = loc.event?.id ?? null;
            const locStorageId = loc.storageLocation?.id ?? null;
            return locEventId === eventId && locStorageId === storageLocationId;
        }) ?? null
    );
}

type SourceDestData = TaskSourceDestRow;

function getTaskIds(task: PackingTask) {
    const itemTypeId = task.itemType?.id;
    const fromEventId = task.fromEvent?.id || null;
    const toEventId = task.toEvent?.id || null;
    const fromStorageLocationId = task.fromStorageLocation?.id || null;
    const toStorageLocationId = task.toStorageLocation?.id || null;
    return { itemTypeId, fromEventId, toEventId, fromStorageLocationId, toStorageLocationId };
}

function fetchTaskSourceDestSync(task: PackingTask, checked: boolean, allItemLocations: ItemLocationForLookup[]): SourceDestData {
    const { itemTypeId, fromEventId, toEventId, fromStorageLocationId, toStorageLocationId } = getTaskIds(task);
    if (!itemTypeId) throw new Error('Item type not found');
    const quantity = task.quantity;
    const quantityChange = checked ? quantity : -quantity;

    const existingSource = findItemLocation(allItemLocations, itemTypeId, fromEventId, fromStorageLocationId);
    const existingDest = findItemLocation(allItemLocations, itemTypeId, toEventId, toStorageLocationId);
    const sourceLocationId = existingSource?.id ?? id();
    const destLocationId = existingDest?.id ?? id();
    const sourceCurrentQty = existingSource?.quantity ?? 0;
    const destCurrentQty = existingDest?.quantity ?? 0;

    return {
        taskId: task.id,
        quantity,
        quantityChange,
        sourceLocationId,
        existingSource,
        sourceCurrentQty,
        destLocationId,
        existingDest,
        destCurrentQty,
        itemTypeId,
        fromEventId,
        toEventId,
        fromStorageLocationId,
        toStorageLocationId,
    };
}

/**
 * Batch update many tasks in a single transaction. Uses preloaded item location
 * data (from useQuery) so it works fully offline without any queryOnce calls.
 */
export async function batchUpdateTaskCompletions(
    tasks: PackingTask[],
    checked: boolean,
    allItemLocations: ItemLocationForLookup[]
): Promise<void> {
    const safeTasks = filterValidPackingTaskEndpoints(tasks);
    const safeLocations = filterValidItemLocations(allItemLocations);
    if (safeTasks.length === 0) return;

    const results = safeTasks.map(task => fetchTaskSourceDestSync(task, checked, safeLocations));

    const validation = validateBatchTaskCompletion(results, checked);
    if (!validation.ok) throw new Error(validation.message);

    const { sourceDeltas, destDeltas, newSourceKeys, newDestKeys } = aggregateTaskDeltas(results);

    const txs: any[] = [];
    const now = checked ? new Date() : null;

    for (const task of safeTasks) {
        txs.push(
            db.tx.packingTasks[task.id].update({
                completed: checked,
                completedAt: now,
            })
        );
    }

    for (const [locationId, { currentQty, totalChange }] of Array.from(sourceDeltas.entries())) {
        const newQty = computeSourceQuantityAfterChange(currentQty, totalChange);
        txs.push(db.tx.itemLocations[locationId].update({ quantity: newQty }));
    }

    for (const [, { totalChange, id: locationId, fromEventId, fromStorageLocationId, itemTypeId }] of Array.from(newSourceKeys.entries())) {
        txs.push(db.tx.itemLocations[locationId].update({ quantity: computeNewSourceQuantity(totalChange) }));
        txs.push(db.tx.itemLocations[locationId].link({ itemType: itemTypeId }));
        if (fromEventId) txs.push(db.tx.itemLocations[locationId].link({ event: fromEventId }));
        if (fromStorageLocationId) txs.push(db.tx.itemLocations[locationId].link({ storageLocation: fromStorageLocationId }));
    }

    for (const [locationId, { currentQty, totalChange }] of Array.from(destDeltas.entries())) {
        const newQty = computeDestQuantityAfterChange(currentQty, totalChange);
        txs.push(db.tx.itemLocations[locationId].update({ quantity: newQty }));
    }

    for (const [, { totalChange, id: locationId, toEventId, toStorageLocationId, itemTypeId }] of Array.from(newDestKeys.entries())) {
        txs.push(db.tx.itemLocations[locationId].update({ quantity: computeNewDestQuantity(totalChange) }));
        txs.push(db.tx.itemLocations[locationId].link({ itemType: itemTypeId }));
        if (toEventId) txs.push(db.tx.itemLocations[locationId].link({ event: toEventId }));
        if (toStorageLocationId) txs.push(db.tx.itemLocations[locationId].link({ storageLocation: toStorageLocationId }));
    }

    await db.transact(txs);
}

/**
 * Update a single packing task's completion state (and related inventory).
 * Uses preloaded item location data (from useQuery) so it works fully offline
 * without any queryOnce calls.
 */
export async function updateTaskCompletion(task: PackingTask, checked: boolean, allItemLocations: ItemLocationForLookup[]): Promise<void> {
    const itemTypeId = task.itemType?.id;
    if (!itemTypeId) {
        throw new Error('Item type not found');
    }

    const fromEventId = task.fromEvent?.id || null;
    const toEventId = task.toEvent?.id || null;
    const fromStorageLocationId = task.fromStorageLocation?.id || null;
    const toStorageLocationId = task.toStorageLocation?.id || null;

    if (!fromEventId && !fromStorageLocationId) {
        throw new Error('Invalid packing task: missing source (event or storage location)');
    }

    const safeLocations = filterValidItemLocations(allItemLocations);

    const quantity = task.quantity;
    const quantityChange = checked ? quantity : -quantity;

    const existingSourceLocation = findItemLocation(safeLocations, itemTypeId, fromEventId, fromStorageLocationId);
    let sourceLocationId: string;

    if (existingSourceLocation) {
        sourceLocationId = existingSourceLocation.id;
        const currentQuantity = existingSourceLocation.quantity || 0;
        const newQuantity = currentQuantity - quantityChange;

        if (checked && newQuantity < 0) {
            throw new Error(`Insufficient inventory at source. Available: ${currentQuantity}, Required: ${quantity}`);
        }
    } else {
        sourceLocationId = id();
        if (checked) {
            throw new Error('Source location does not exist');
        }
    }

    const existingDestLocation = findItemLocation(safeLocations, itemTypeId, toEventId, toStorageLocationId);
    const destLocationId = existingDestLocation?.id ?? id();

    const txs: any[] = [];

    txs.push(
        db.tx.packingTasks[task.id].update({
            completed: checked,
            completedAt: checked ? new Date() : null,
        })
    );

    if (existingSourceLocation) {
        const currentQuantity = existingSourceLocation.quantity || 0;
        const newQuantity = computeSourceQuantityAfterChange(currentQuantity, quantityChange);
        txs.push(
            db.tx.itemLocations[sourceLocationId].update({
                quantity: newQuantity,
            })
        );
    } else {
        txs.push(
            db.tx.itemLocations[sourceLocationId].update({
                quantity: computeNewSourceQuantity(quantityChange),
            })
        );
        txs.push(db.tx.itemLocations[sourceLocationId].link({ itemType: itemTypeId }));
        if (fromEventId) {
            txs.push(db.tx.itemLocations[sourceLocationId].link({ event: fromEventId }));
        }
        if (fromStorageLocationId) {
            txs.push(db.tx.itemLocations[sourceLocationId].link({ storageLocation: fromStorageLocationId }));
        }
    }

    if (existingDestLocation) {
        const currentQuantity = existingDestLocation.quantity || 0;
        const newQuantity = computeDestQuantityAfterChange(currentQuantity, quantityChange);
        txs.push(
            db.tx.itemLocations[destLocationId].update({
                quantity: newQuantity,
            })
        );
    } else {
        txs.push(
            db.tx.itemLocations[destLocationId].update({
                quantity: computeNewDestQuantity(quantityChange),
            })
        );
        txs.push(db.tx.itemLocations[destLocationId].link({ itemType: itemTypeId }));
        if (toEventId) {
            txs.push(db.tx.itemLocations[destLocationId].link({ event: toEventId }));
        }
        if (toStorageLocationId) {
            txs.push(db.tx.itemLocations[destLocationId].link({ storageLocation: toStorageLocationId }));
        }
    }

    await db.transact(txs);
}
